import React, { useMemo, useState } from "react";
import { formatarHora } from "../../services/conversaoTimer";
import { FaCheck, FaCheckDouble, FaQrcode } from "react-icons/fa";
import ModalCheckinQr from "../agenda/modalCheckinQr";
import ModalPesquisa from "../../components/modal/modalPesquisa";
import api from "../../services/axios";
import { toast } from "react-toastify";

function ehPatrocinador(participante) {
  const usuario = participante?.usuario;
  if (!usuario) return false;
  if (usuario.tipo === "patrocinador") return true;
  return Boolean(usuario.id_patrocinador);
}

function ehConvidado(participante) {
  const usuario = participante?.usuario;
  if (!usuario) return false;
  if (usuario.tipo === "convidado") return true;
  return !usuario.id_patrocinador;
}

function separarParticipantes(participantes) {
  const lista = participantes || [];
  const patrocinadores = lista.filter(ehPatrocinador);
  const convidados = lista.filter(ehConvidado);

  if (!patrocinadores.length && !convidados.length) {
    return { patrocinadores: [], convidados: lista };
  }

  return { patrocinadores, convidados };
}

function nomesParticipantes(participantes) {
  return participantes
    .map((p) => p?.usuario?.nome)
    .filter(Boolean)
    .join(" / ");
}

function formatarTituloMm(patrocinadores, convidados) {
  const nomePatrocinador = nomesParticipantes(patrocinadores);
  const nomeConvidado = nomesParticipantes(convidados);

  if (nomePatrocinador && nomeConvidado) {
    return `${nomePatrocinador} - ${nomeConvidado}`;
  }

  return nomePatrocinador || nomeConvidado || "—";
}

function formatarHorarioFaixa(inicio, fim) {
  const horaInicio = formatarHora(inicio)?.substring(0, 5) || "";
  const horaFim = formatarHora(fim)?.substring(0, 5) || "";
  if (!horaInicio && !horaFim) return "";
  if (!horaFim) return horaInicio;
  return `${horaInicio} - ${horaFim}`;
}

function LinhaMM({ item, handle }) {
  const [showModalQr, setShowModalQr] = useState(false);
  const [showModalPesquisa, setShowModalPesquisa] = useState(false);
  const [pesquisa, setPesquisa] = useState(null);
  const [participante, setParticipante] = useState(null);
  const [verificandoPesquisa, setVerificandoPesquisa] = useState(false);

  const itemParaQr = useMemo(
    () => ({
      id_agenda: item.id,
      agenda: item,
      ...item,
    }),
    [item]
  );

  const { patrocinadores, convidados } = useMemo(
    () => separarParticipantes(item.participantes || []),
    [item.participantes]
  );

  const convidado = convidados[0] || null;

  const titulo = useMemo(
    () => formatarTituloMm(patrocinadores, convidados),
    [patrocinadores, convidados]
  );

  const metaConvidado = useMemo(() => {
    const usuario = convidado?.usuario;
    if (!usuario) return "";
    return [usuario.cargo, usuario.empresa].filter(Boolean).join(" · ");
  }, [convidado]);

  const reuniaoJaIniciada = Boolean(
    item.participantes?.some((p) => p.presenca)
  );

  const todosParticipantesComRate = Boolean(
    item.participantes?.length &&
      item.participantes.every((p) => p.presenca && p.rate)
  );

  if (!item.participantes?.[0]) {
    return null;
  }

  const abrirLeitorQr = (e) => {
    e?.stopPropagation?.();
    setShowModalQr(true);
  };

  const fecharLeitorQr = () => {
    setShowModalQr(false);
    handle?.();
  };

  const handleModalPesquisa = () => {
    setShowModalPesquisa((prev) => !prev);
  };

  const abrirPesquisaReuniao = async () => {
    const idUsuario =
      convidado?.id_usuario ?? convidado?.usuario?.id ?? null;
    if (!idUsuario || verificandoPesquisa) return;

    setVerificandoPesquisa(true);
    try {
      const resp = await api.post("/verifica-pesquisa-usuario", {
        id_usuario: idUsuario,
      });

      if (resp.status !== 200) return;

      if (!resp.data.pesquisas?.length) {
        toast.info("Nenhuma pesquisa pendente para este usuário");
        return;
      }

      const pesquisaMM = resp.data.pesquisas.filter(
        (p) => p.agenda?.id === item.id
      );

      if (!pesquisaMM.length) {
        toast.info("Nenhuma pesquisa pendente para esta reunião");
        return;
      }

      const outroParticipante =
        patrocinadores[0] ||
        item.participantes?.find((p) => p.id_usuario !== idUsuario) ||
        null;

      setParticipante(outroParticipante);
      setPesquisa(pesquisaMM[0]);
      setShowModalPesquisa(true);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao verificar pesquisa pendente.");
    } finally {
      setVerificandoPesquisa(false);
    }
  };

  const handleClickLinha = () => {
    if (!reuniaoJaIniciada) return;
    abrirPesquisaReuniao();
  };

  return (
    <>
      <ModalCheckinQr
        titulo="Check-in M&M"
        show={showModalQr}
        toggleModal={fecharLeitorQr}
        item={itemParaQr}
        modo="m_m"
        onCheckinConfirmado={() => handle?.()}
      />

      <ModalPesquisa
        titulo="Pesquisa"
        show={showModalPesquisa}
        toggleModal={handleModalPesquisa}
        pesquisa={pesquisa}
        tipoUsuario={"convidado"}
        texto={
          "Para liberar a entrada na proxima reunião por favor nos diga o que achou"
        }
        close={true}
        participante={participante}
        onRespondido={() => handle?.()}
      />

      <div
        className={`linha-agenda${reuniaoJaIniciada ? " mm-linha--clicavel" : ""}`}
        onClick={handleClickLinha}
        role={reuniaoJaIniciada ? "button" : undefined}
        tabIndex={reuniaoJaIniciada ? 0 : undefined}
        onKeyDown={(e) => {
          if (reuniaoJaIniciada && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleClickLinha();
          }
        }}
        title={
          reuniaoJaIniciada
            ? "Abrir pesquisa da reunião para o convidado"
            : undefined
        }
      >
        <div className="mm-linha-topo">
          {reuniaoJaIniciada ? (
            <div
              className={`mm-status-badge${
                todosParticipantesComRate ? " is-completo" : ""
              }`}
              title={
                todosParticipantesComRate
                  ? "Check-in e pesquisas concluídos"
                  : "Reunião iniciada"
              }
            >
              {todosParticipantesComRate ? <FaCheckDouble /> : <FaCheck />}
            </div>
          ) : null}

          <div className="mm-linha-info">
            <div className="mm-convidado">
              <div className="mm-convidado-nome">{titulo}</div>
              {metaConvidado ? (
                <div className="mm-convidado-meta">{metaConvidado}</div>
              ) : null}
            </div>
            <div className="label-hora mm-linha-hora">
              {formatarHorarioFaixa(item.dt_hora_inicio, item.dt_hora_fim)}
            </div>
          </div>

          {!reuniaoJaIniciada ? (
            <button
              type="button"
              className="apl-btn apl-btn--primary mm-btn-qr"
              onClick={abrirLeitorQr}
              title="Abrir leitor de QR Code"
            >
              <FaQrcode size={14} aria-hidden />
              QR Code
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default LinhaMM;
